'use client'
import * as React from 'react'
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react'
import { DayPicker, type DateRange, DayButton, getDefaultClassNames } from 'react-day-picker'
import { format, startOfMonth, endOfMonth, addMonths, addYears, subYears, startOfYear, endOfYear } from 'date-fns'
import { cn } from '../../helpers/classnames'
import { JosefFormLabel } from '../form-label/josef-form-label'

// Custom day button component with Josef theming
function JosefDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()
  const ref = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        // Base styles
        'relative h-9 w-9 p-0 font-normal aria-selected:opacity-100',
        'inline-flex items-center justify-center rounded-md text-sm',
        'transition-colors focus-visible:outline-none focus-visible:ring-1',
        'focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100',
        'disabled:pointer-events-none disabled:opacity-50',

        // Default state
        'hover:bg-gray-100 dark:hover:bg-white/10',
        'text-gray-900 dark:text-gray-100',

        // Selected states
        'data-[selected-single=true]:bg-black dark:data-[selected-single=true]:bg-white',
        'data-[selected-single=true]:text-white dark:data-[selected-single=true]:text-black',
        'data-[selected-single=true]:hover:bg-gray-800 dark:data-[selected-single=true]:hover:bg-gray-200',

        // Range states
        'data-[range-start=true]:bg-black dark:data-[range-start=true]:bg-white',
        'data-[range-start=true]:text-white dark:data-[range-start=true]:text-black',
        'data-[range-start=true]:rounded-l-md data-[range-start=true]:rounded-r-none',

        'data-[range-middle=true]:bg-gray-100 dark:data-[range-middle=true]:bg-white/10',
        'data-[range-middle=true]:text-gray-900 dark:data-[range-middle=true]:text-gray-100',
        'data-[range-middle=true]:rounded-none',

        'data-[range-end=true]:bg-black dark:data-[range-end=true]:bg-white',
        'data-[range-end=true]:text-white dark:data-[range-end=true]:text-black',
        'data-[range-end=true]:rounded-r-md data-[range-end=true]:rounded-l-none',

        className
      )}
      {...props}
    >
      {day.date.getDate()}
    </button>
  )
}

export interface JosefDatePickerProps {
  mode?: 'single' | 'range' | 'multiple'
  value?: Date | DateRange | Date[] | undefined
  onChange?: (dates: Date | DateRange | Date[] | undefined) => void
  maxDates?: number
  label?: string
  error?: string
  placeholder?: string
  className?: string
  buttonClassName?: string
  disabled?: boolean
  numberOfMonths?: number
  branded?: boolean
  size?: 'sm' | 'md' | 'lg'
  monthOnly?: boolean
  align?: 'start' | 'center' | 'end'
  /**
   * Custom date-fns format string for the single-date trigger label
   * (e.g. 'EEE, d MMM' → "Mon, 22 Jun"). Defaults to 'EEE, MMM d, yyyy'.
   */
  displayFormat?: string
}

const sizeClasses = {
  sm: 'h-8 px-3 py-1 text-xs',
  md: 'h-10 px-3 py-2 text-sm',
  lg: 'h-12 px-4 py-3 text-base',
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function JosefDatePicker({
  mode = 'range',
  value,
  onChange,
  maxDates = 10,
  label,
  error,
  placeholder = 'Select dates...',
  className,
  buttonClassName,
  disabled = false,
  numberOfMonths = mode === 'range' ? 2 : 1,
  branded = false,
  size = 'md',
  monthOnly = false,
  align = 'end',
  displayFormat,
}: JosefDatePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [internalValue, setInternalValue] = React.useState<Date | DateRange | Date[] | undefined>(value)
  const [viewYear, setViewYear] = React.useState(() => {
    if (value && mode === 'single' && value instanceof Date) {
      return startOfYear(value)
    }
    if (value && mode === 'range' && 'from' in (value as DateRange) && (value as DateRange).from) {
      return startOfYear((value as DateRange).from!)
    }
    return startOfYear(new Date())
  })
  const defaultClassNames = getDefaultClassNames()

  React.useEffect(() => {
    setInternalValue(value)
  }, [value])

  const handleSelect = (dates: Date | DateRange | Date[] | undefined) => {
    if (mode === 'single') {
      setInternalValue(dates)
      onChange?.(dates)
      setIsOpen(false)
    } else {
      // multiple mode
      setInternalValue(dates)
      onChange?.(dates)
    }
  }

  const formatDisplayValue = () => {
    if (!internalValue) return placeholder

    if (mode === 'single') {
      const date = internalValue as Date
      if (date) {
        if (displayFormat) return format(date, displayFormat)
        return monthOnly ? format(date, 'MMMM yyyy') : format(date, 'EEE, MMM d, yyyy')
      }
    } else if (mode === 'range') {
      const range = internalValue as DateRange
      if (range?.from) {
        if (range.to) {
          if (monthOnly) {
            return `${format(range.from, 'MMM yyyy')} - ${format(range.to, 'MMM yyyy')}`
          }
          return `${format(range.from, 'MMM d, yyyy')} - ${format(range.to, 'MMM d, yyyy')}`
        }
        return monthOnly ? format(range.from, 'MMMM yyyy') : format(range.from, 'MMM d, yyyy')
      }
    } else {
      const dates = internalValue as Date[]
      if (dates?.length > 0) {
        if (dates.length === 1) {
          return monthOnly ? format(dates[0], 'MMMM yyyy') : format(dates[0], 'MMM d, yyyy')
        }
        return `${dates.length} ${monthOnly ? 'months' : 'dates'} selected`
      }
    }

    return placeholder
  }

  const handleMonthSelect = (monthIndex: number) => {
    const selectedMonth = addMonths(viewYear, monthIndex)
    const monthStart = startOfMonth(selectedMonth)
    const monthEnd = endOfMonth(selectedMonth)

    if (mode === 'single') {
      setInternalValue(monthStart)
      onChange?.(monthStart)
      setIsOpen(false)
    } else if (mode === 'range') {
      const range: DateRange = { from: monthStart, to: monthEnd }
      setInternalValue(range)
      onChange?.(range)
      setIsOpen(false)
    }
  }

  const isMonthSelected = (monthIndex: number) => {
    const monthDate = addMonths(viewYear, monthIndex)
    if (mode === 'single' && internalValue instanceof Date) {
      return (
        internalValue.getMonth() === monthDate.getMonth() &&
        internalValue.getFullYear() === monthDate.getFullYear()
      )
    }
    if (mode === 'range' && internalValue && 'from' in internalValue) {
      const range = internalValue as DateRange
      if (range.from) {
        return (
          range.from.getMonth() === monthDate.getMonth() &&
          range.from.getFullYear() === monthDate.getFullYear()
        )
      }
    }
    return false
  }

  const isMonthDisabled = (monthIndex: number) => {
    const monthDate = addMonths(viewYear, monthIndex)
    return monthDate > new Date()
  }

  return (
    <div className={cn('space-y-1', className)}>
      {label && <JosefFormLabel>{label}</JosefFormLabel>}

      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(
            'flex w-full items-center justify-between rounded-lg',
            sizeClasses[size],
            branded ? [
              'border border-neutral-300 dark:border-neutral-600',
              'bg-white dark:bg-neutral-800',
              'text-neutral-900 dark:text-neutral-100',
              'placeholder:text-neutral-500 dark:placeholder:text-neutral-400',
              'hover:border-neutral-400 dark:hover:border-neutral-500',
              'focus:outline-none focus:ring-2 focus:ring-[var(--brand-color)] focus:border-transparent',
            ] : [
              'bg-white dark:bg-black',
              'text-gray-900 dark:text-white',
              'placeholder:text-gray-400 dark:placeholder:text-gray-500',
              'hover:bg-black/5 dark:hover:bg-white/5',
              'focus:outline-none',
            ],
            'disabled:cursor-not-allowed disabled:opacity-50',
            error && 'border border-red-500 dark:border-red-500',
            buttonClassName
          )}
        >
          <span className={cn(!internalValue && 'text-gray-400 dark:text-gray-500')}>
            {formatDisplayValue()}
          </span>
          <CalendarIcon className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0" />
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <div
              className={cn(
                'absolute top-full z-50 mt-2 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#191919] shadow-lg p-4',
                align === 'start' && 'left-0',
                align === 'center' && 'left-1/2 -translate-x-1/2',
                align === 'end' && 'right-0'
              )}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              {monthOnly ? (
                <div className="w-[240px]">
                  <div className="flex items-center justify-between mb-4">
                    <button
                      type="button"
                      onClick={() => setViewYear(subYears(viewYear, 1))}
                      className={cn(
                        'h-7 w-7 p-0',
                        'inline-flex items-center justify-center rounded-md',
                        'bg-black dark:bg-white text-white dark:text-black',
                        'hover:bg-gray-800 dark:hover:bg-gray-200',
                        'transition-colors'
                      )}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {format(viewYear, 'yyyy')}
                    </span>
                    <button
                      type="button"
                      onClick={() => setViewYear(addYears(viewYear, 1))}
                      disabled={addYears(viewYear, 1) > endOfYear(new Date())}
                      className={cn(
                        'h-7 w-7 p-0',
                        'inline-flex items-center justify-center rounded-md',
                        'bg-black dark:bg-white text-white dark:text-black',
                        'hover:bg-gray-800 dark:hover:bg-gray-200',
                        'transition-colors',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      )}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {MONTHS.map((month, index) => (
                      <button
                        key={month}
                        type="button"
                        onClick={() => handleMonthSelect(index)}
                        disabled={isMonthDisabled(index)}
                        className={cn(
                          'h-9 px-2 rounded-md text-sm font-medium transition-colors',
                          isMonthSelected(index)
                            ? 'bg-black dark:bg-white text-white dark:text-black'
                            : 'hover:bg-gray-100 dark:hover:bg-white/10 text-gray-900 dark:text-gray-100',
                          'disabled:opacity-50 disabled:cursor-not-allowed'
                        )}
                      >
                        {month}
                      </button>
                    ))}
                  </div>
                </div>
              ) : mode === 'range' ? (
                <div className="flex flex-col gap-4">
                  <DayPicker
                    mode="range"
                    selected={internalValue as DateRange}
                    onSelect={(range) => setInternalValue(range)}
                    numberOfMonths={numberOfMonths}
                    showOutsideDays
                    classNames={{
                    root: cn('w-fit', defaultClassNames.root),
                    months: cn(
                      'flex gap-6 flex-col sm:flex-row',
                      defaultClassNames.months
                    ),
                    month: cn('flex flex-col gap-4', defaultClassNames.month),
                    nav: cn(
                      'flex items-center gap-1 w-full absolute top-6 inset-x-0 justify-between',
                      defaultClassNames.nav
                    ),
                    button_previous: cn(
                      'h-7 w-7 p-0',
                      'inline-flex items-center justify-center rounded-md',
                      'bg-black dark:bg-white text-white dark:text-black',
                      'hover:bg-gray-800 dark:hover:bg-gray-200',
                      'transition-colors',
                      'absolute left-2',
                      defaultClassNames.button_previous
                    ),
                    button_next: cn(
                      'h-7 w-7 p-0',
                      'inline-flex items-center justify-center rounded-md',
                      'bg-black dark:bg-white text-white dark:text-black',
                      'hover:bg-gray-800 dark:hover:bg-gray-200',
                      'transition-colors',
                      'absolute right-2',
                      defaultClassNames.button_next
                    ),
                    month_caption: cn(
                      'flex items-center justify-center h-7 px-10',
                      defaultClassNames.month_caption
                    ),
                    caption_label: cn(
                      'text-sm font-medium',
                      'text-gray-900 dark:text-gray-100',
                      defaultClassNames.caption_label
                    ),
                    table: 'w-full border-collapse',
                    weekdays: cn('flex', defaultClassNames.weekdays),
                    weekday: cn(
                      'text-gray-500 dark:text-gray-400 rounded-md w-9 font-normal text-[0.8rem]',
                      defaultClassNames.weekday
                    ),
                    week: cn('flex w-full mt-2', defaultClassNames.week),
                    day: cn(
                      'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
                      '[&:has([aria-selected])]:bg-gray-100 dark:[&:has([aria-selected])]:bg-white/10',
                      '[&:has([aria-selected].day-outside)]:bg-gray-100/50 dark:[&:has([aria-selected].day-outside)]:bg-white/5',
                      '[&:has([aria-selected].day-range-start)]:rounded-l-md',
                      '[&:has([aria-selected].day-range-end)]:rounded-r-md',
                      defaultClassNames.day
                    ),
                    range_start: 'day-range-start',
                    range_middle: cn(
                      '[&:has([aria-selected])]:bg-gray-100 dark:[&:has([aria-selected])]:bg-white/10',
                      defaultClassNames.range_middle
                    ),
                    range_end: 'day-range-end',
                    today: cn(
                      'bg-gray-100 dark:bg-white/10',
                      'text-gray-900 dark:text-gray-100',
                      defaultClassNames.today
                    ),
                    outside: cn(
                      'text-gray-400 dark:text-gray-600 opacity-50',
                      'aria-selected:bg-gray-100/50 dark:aria-selected:bg-white/5',
                      'aria-selected:text-gray-400 dark:aria-selected:text-gray-600',
                      defaultClassNames.outside
                    ),
                    disabled: cn(
                      'text-gray-400 dark:text-gray-600 opacity-50',
                      defaultClassNames.disabled
                    ),
                    hidden: 'invisible',
                  }}
                  components={{
                    Chevron: ({ orientation }) => {
                      if (orientation === 'left') {
                        return <ChevronLeft className="h-4 w-4" />
                      }
                      return <ChevronRight className="h-4 w-4" />
                    },
                    DayButton: JosefDayButton,
                  }}
                />
                  <div className="flex items-center justify-between pt-3 border-t border-black/10 dark:border-white/10">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {(internalValue as DateRange)?.from && (internalValue as DateRange)?.to
                        ? `${format((internalValue as DateRange).from!, 'MMM d')} - ${format((internalValue as DateRange).to!, 'MMM d, yyyy')}`
                        : (internalValue as DateRange)?.from
                          ? `${format((internalValue as DateRange).from!, 'MMM d, yyyy')} - ...`
                          : 'Select date range'}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const range = internalValue as DateRange
                        if (range?.from) {
                          const finalRange = { from: range.from, to: range.to ?? range.from }
                          onChange?.(finalRange)
                          setIsOpen(false)
                        }
                      }}
                      disabled={!(internalValue as DateRange)?.from}
                      className={cn(
                        'px-4 py-1.5 text-sm font-medium rounded-md transition-colors',
                        'bg-black dark:bg-white text-white dark:text-black',
                        'hover:bg-gray-800 dark:hover:bg-gray-200',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      )}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              ) : (
                <DayPicker
                  mode={mode as any}
                  selected={internalValue as any}
                  onSelect={handleSelect as any}
                  numberOfMonths={numberOfMonths}
                  max={mode === 'multiple' ? maxDates : undefined}
                  showOutsideDays
                  classNames={{
                    root: cn('w-fit', defaultClassNames.root),
                    months: cn(
                      'flex gap-6 flex-col sm:flex-row',
                      defaultClassNames.months
                    ),
                    month: cn('flex flex-col gap-4', defaultClassNames.month),
                    nav: cn(
                      'flex items-center gap-1 w-full absolute top-6 inset-x-0 justify-between',
                      defaultClassNames.nav
                    ),
                    button_previous: cn(
                      'h-7 w-7 p-0',
                      'inline-flex items-center justify-center rounded-md',
                      'bg-black dark:bg-white text-white dark:text-black',
                      'hover:bg-gray-800 dark:hover:bg-gray-200',
                      'transition-colors',
                      'absolute left-2',
                      defaultClassNames.button_previous
                    ),
                    button_next: cn(
                      'h-7 w-7 p-0',
                      'inline-flex items-center justify-center rounded-md',
                      'bg-black dark:bg-white text-white dark:text-black',
                      'hover:bg-gray-800 dark:hover:bg-gray-200',
                      'transition-colors',
                      'absolute right-2',
                      defaultClassNames.button_next
                    ),
                    month_caption: cn(
                      'flex items-center justify-center h-7 px-10',
                      defaultClassNames.month_caption
                    ),
                    caption_label: cn(
                      'text-sm font-medium',
                      'text-gray-900 dark:text-gray-100',
                      defaultClassNames.caption_label
                    ),
                    table: 'w-full border-collapse',
                    weekdays: cn('flex', defaultClassNames.weekdays),
                    weekday: cn(
                      'text-gray-500 dark:text-gray-400 rounded-md w-9 font-normal text-[0.8rem]',
                      defaultClassNames.weekday
                    ),
                    week: cn('flex w-full mt-2', defaultClassNames.week),
                    day: cn(
                      'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
                      '[&:has([aria-selected])]:bg-gray-100 dark:[&:has([aria-selected])]:bg-white/10',
                      '[&:has([aria-selected].day-outside)]:bg-gray-100/50 dark:[&:has([aria-selected].day-outside)]:bg-white/5',
                      '[&:has([aria-selected].day-range-start)]:rounded-l-md',
                      '[&:has([aria-selected].day-range-end)]:rounded-r-md',
                      defaultClassNames.day
                    ),
                    range_start: 'day-range-start',
                    range_middle: cn(
                      '[&:has([aria-selected])]:bg-gray-100 dark:[&:has([aria-selected])]:bg-white/10',
                      defaultClassNames.range_middle
                    ),
                    range_end: 'day-range-end',
                    today: cn(
                      'bg-gray-100 dark:bg-white/10',
                      'text-gray-900 dark:text-gray-100',
                      defaultClassNames.today
                    ),
                    outside: cn(
                      'text-gray-400 dark:text-gray-600 opacity-50',
                      'aria-selected:bg-gray-100/50 dark:aria-selected:bg-white/5',
                      'aria-selected:text-gray-400 dark:aria-selected:text-gray-600',
                      defaultClassNames.outside
                    ),
                    disabled: cn(
                      'text-gray-400 dark:text-gray-600 opacity-50',
                      defaultClassNames.disabled
                    ),
                    hidden: 'invisible',
                  }}
                  components={{
                    Chevron: ({ orientation }) => {
                      if (orientation === 'left') {
                        return <ChevronLeft className="h-4 w-4" />
                      }
                      return <ChevronRight className="h-4 w-4" />
                    },
                    DayButton: JosefDayButton,
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
      )}
    </div>
  )
}